import React, { useState, useEffect, useMemo, useCallback } from "react";
import { 
  FaTrashAlt, FaEye, FaSearch, FaEnvelope, FaPhone, FaCalendar, 
  FaMapMarkerAlt, FaUser, FaLink, FaTimes, FaBuilding, FaCheck, 
  FaTimes as FaTimesIcon, FaKey, FaExclamationTriangle, FaCheckCircle,
  FaUserCircle, FaSort, FaSortUp, FaSortDown, FaFile
} from "react-icons/fa";
import { useAllOrganizations, useDeleteUser, useResetUserPassword, useUserDetails } from "../../hooks/useAdmin.ts";
import Pagination from "../../components/Pagination.tsx";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";
import PasswordResetModal from "../../components/common/PasswordResetModal.tsx";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnDef,
} from "@tanstack/react-table";
import toast from "react-hot-toast";

interface Organization {
  id: number;
  user_id: number;
  email: string;
  role: string;
  profile: {
    organization_name: string;
    error?: string;
  };
}

interface OrganizationProfile {
  user: number;
  organization_name: string;
  street: string;
  city: string;
  state: string;
  pincode: string;
  landmark: string;
  address: string;
  phone_number: string;
  is_verified: boolean;
  profile_picture: string;
  documents: string[];
}

interface OrganizationDetailsResponse {
  profile: OrganizationProfile;
}

const AdminOrganizations: React.FC = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  
  // State
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [isProfileModalOpen, setProfileModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'verified' | 'pending' | 'error'>('all');

  // Hooks
  const { data: orgsData, isLoading: isLoadingOrgs } = useAllOrganizations();
  const deleteMutation = useDeleteUser();
  const resetPasswordMutation = useResetUserPassword();
  
  // Fetch details when selectedOrg is set
  const { 
    data: orgDetails, 
    isLoading: isDetailsLoading, 
    error: detailsError 
  } = useUserDetails(selectedOrg?.user_id || 0);

  const url = process.env.REACT_APP_DB_URI;

  // Sync orgsData to local state
  useEffect(() => {
    if (orgsData) {
      setOrganizations(orgsData);
      setFilteredOrganizations(orgsData);
    }
  }, [orgsData]);

  // Filter and search
  useEffect(() => {
    let filtered = [...organizations];

    // Apply search
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(org => {
        const name = org?.profile?.organization_name?.toLowerCase() || "";
        const email = org.email.toLowerCase();
        return name.includes(search) || email.includes(search);
      });
    }

    // Apply status filter
    if (filterStatus === 'verified') {
      filtered = filtered.filter(org => 
        org.profile && !org.profile.error && org.profile.is_verified === true
      );
    } else if (filterStatus === 'pending') {
      filtered = filtered.filter(org => 
        org.profile && !org.profile.error && org.profile.is_verified === false
      );
    } else if (filterStatus === 'error') {
      filtered = filtered.filter(org => 
        org.profile?.error === "Decryption Failed"
      );
    }

    setFilteredOrganizations(filtered);
    setCurrentPage(1);
  }, [searchTerm, organizations, filterStatus]);

  const getFullImageUrl = (imagePath: string | null) => {
    if (!imagePath) return "/default-avatar.png";
    if (imagePath.startsWith('http')) return imagePath;
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    const baseUrl = process.env.REACT_APP_PF || url;
    return `${baseUrl}/${cleanPath}`;
  };

  const handleView = (org: Organization) => {
    setSelectedOrg(org);
    setProfileModalOpen(true);
  };

  const handleDelete = (id: number) => {
    setOrgToDelete(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!orgToDelete) return;

    try {
      await deleteMutation.mutateAsync(orgToDelete);
      setOrganizations(prev => prev.filter(o => o.user_id !== orgToDelete));
      setDeleteModalOpen(false);
      setOrgToDelete(null);
      toast.success("Organization deleted successfully");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete organization");
    }
  };

  const closeModal = () => {
    setProfileModalOpen(false);
    setSelectedOrg(null);
  };

  const handlePasswordReset = async (newPassword: string, confirmPassword: string) => {
    if (!selectedOrg) throw new Error("No organization selected");

    try {
      await resetPasswordMutation.mutateAsync({
        userId: selectedOrg.user_id,
        newPassword,
        confirmPassword
      });
      toast.success("Password reset successfully");
    } catch (err) {
      toast.error("Failed to reset password");
      throw err;
    }
  };

  // Stats
  const stats = {
    total: organizations.length,
    verified: organizations.filter(o => o.profile && !o.profile.error && o.profile.is_verified).length,
    pending: organizations.filter(o => o.profile && !o.profile.error && !o.profile.is_verified).length,
    error: organizations.filter(o => o.profile?.error === "Decryption Failed").length,
  };

  // Table columns
  const columnHelper = createColumnHelper<Organization>();

  const columns: ColumnDef<Organization, any>[] = [
    columnHelper.accessor('profile.organization_name', {
      header: 'Organization',
      cell: (info) => {
        const hasError = info.row.original.profile?.error;
        return (
          <div className="flex items-center gap-2">
            <FaBuilding className={hasError ? 'text-red-400' : 'text-gray-400'} />
            <span style={{ color: hasError ? currentTheme.error : currentTheme.text }}>
              {hasError ? 'Decryption Error' : info.getValue() || 'N/A'}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: (info) => (
        <div className="flex items-center gap-2">
          <FaEnvelope className="text-gray-400" size={12} />
          <span style={{ color: currentTheme.text }}>{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.display({
      id: 'status',
      header: 'Status',
      cell: (props) => {
        const org = props.row.original;
        if (org.profile?.error) {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                  style={{ backgroundColor: `${currentTheme.error}20`, color: currentTheme.error }}>
              <FaExclamationTriangle size={10} /> Decryption Error
            </span>
          );
        }
        return (
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
            org.profile?.is_verified 
              ? 'bg-green-100 text-green-700' 
              : 'bg-yellow-100 text-yellow-700'
          }`}>
            {org.profile?.is_verified ? (
              <><FaCheck size={10} /> Verified</>
            ) : (
              <><FaTimesIcon size={10} /> Pending</>
            )}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (props) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleView(props.row.original)}
            className="p-2 rounded-lg transition-colors hover:opacity-80"
            style={{ color: currentTheme.primary, backgroundColor: `${currentTheme.primary}20` }}
            title="View Details"
          >
            <FaEye size={16} />
          </button>
          <button
            onClick={() => handleDelete(props.row.original.user_id)}
            className="p-2 rounded-lg transition-colors hover:opacity-80"
            style={{ color: currentTheme.error, backgroundColor: `${currentTheme.error}20` }}
            title="Delete Organization"
          >
            <FaTrashAlt size={16} />
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: filteredOrganizations,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrganizations.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedRows = table.getRowModel().rows.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className="pb-4" style={{ backgroundColor: currentTheme.background }}>
      
      {/* Stats Cards */}
      {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="p-4 rounded-lg shadow-sm" style={{ backgroundColor: currentTheme.surface }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentTheme.textSecondary }}>Total</p>
              <p className="text-2xl font-bold" style={{ color: currentTheme.text }}>{stats.total}</p>
            </div>
            <FaBuilding className="text-3xl opacity-50" style={{ color: currentTheme.primary }} />
          </div>
        </div>
        
        <div className="p-4 rounded-lg shadow-sm" style={{ backgroundColor: currentTheme.surface }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentTheme.textSecondary }}>Verified</p>
              <p className="text-2xl font-bold" style={{ color: currentTheme.success }}>{stats.verified}</p>
            </div>
            <FaCheckCircle className="text-3xl opacity-50" style={{ color: currentTheme.success }} />
          </div>
        </div>
        
        <div className="p-4 rounded-lg shadow-sm" style={{ backgroundColor: currentTheme.surface }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentTheme.textSecondary }}>Pending</p>
              <p className="text-2xl font-bold" style={{ color: currentTheme.warning }}>{stats.pending}</p>
            </div>
            <FaTimesIcon className="text-3xl opacity-50" style={{ color: currentTheme.warning }} />
          </div>
        </div>
        
        <div className="p-4 rounded-lg shadow-sm" style={{ backgroundColor: currentTheme.surface }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentTheme.textSecondary }}>Errors</p>
              <p className="text-2xl font-bold" style={{ color: currentTheme.error }}>{stats.error}</p>
            </div>
            <FaExclamationTriangle className="text-3xl opacity-50" style={{ color: currentTheme.error }} />
          </div>
        </div>
      </div> */}

      {/* Search and Filters */}
      <div className="mb-4 flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[300px]">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: currentTheme.textSecondary }} />
            <input
              type="text"
              placeholder="Search by organization name or email..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
              style={{ 
                backgroundColor: currentTheme.surface, 
                color: currentTheme.text,
                borderColor: currentTheme.border
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="flex gap-2">
          {(['all', 'verified', 'pending', 'error'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className="px-4 py-2 rounded-lg capitalize transition-colors"
              style={{
                backgroundColor: filterStatus === status ? currentTheme.primary : currentTheme.surface,
                color: filterStatus === status ? currentTheme.inverseText : currentTheme.text,
                border: `1px solid ${currentTheme.border}`
              }}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg shadow-lg overflow-hidden" style={{ backgroundColor: currentTheme.surface }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id} style={{ backgroundColor: currentTheme.primary }}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                      className="px-4 py-3 text-left text-sm font-medium cursor-pointer select-none"
                      style={{ color: currentTheme.inverseText }}
                    >
                      <div className="flex items-center gap-2">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span className="flex flex-col text-[10px] leading-none">
                            <span style={{ opacity: header.column.getIsSorted() === 'asc' ? 1 : 0.5 }}>▲</span>
                            <span style={{ opacity: header.column.getIsSorted() === 'desc' ? 1 : 0.5 }}>▼</span>
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoadingOrgs ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center">
                    <div className="flex justify-center items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: currentTheme.primary }} />
                      <span style={{ color: currentTheme.textSecondary }}>Loading organizations...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedRows.length > 0 ? (
                paginatedRows.map((row, index) => (
                  <tr
                    key={row.id}
                    className="transition-colors hover:opacity-90"
                    style={{ backgroundColor: index % 2 === 0 ? currentTheme.surface : currentTheme.card }}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-4 py-3 text-sm" style={{ color: currentTheme.text }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center" style={{ color: currentTheme.textSecondary }}>
                    {searchTerm ? "No matching organizations found" : "No organizations available"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t" 
             style={{ backgroundColor: currentTheme.surface, borderColor: currentTheme.border }}>
          <div className="flex items-center gap-4">
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-1 rounded border text-sm focus:outline-none focus:ring-2"
              style={{
                backgroundColor: currentTheme.surface,
                color: currentTheme.text,
                borderColor: currentTheme.border
              }}
            >
              {[10, 25, 50, 100].map(value => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
            <span className="text-sm" style={{ color: currentTheme.textSecondary }}>
              Showing {filteredOrganizations.length === 0 ? 0 : startIndex + 1}-
              {Math.min(startIndex + rowsPerPage, filteredOrganizations.length)} of {filteredOrganizations.length}
            </span>
          </div>
          <Pagination 
            currentPage={currentPage} 
            totalPages={totalPages} 
            onPageChange={setCurrentPage} 
          />
        </div>
      </div>

      {/* Details Modal */}
      {isProfileModalOpen && selectedOrg && (
        <div className="fixed inset-0 flex items-center justify-center z-[101]" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="w-[95%] max-w-4xl max-h-[90vh] rounded-lg shadow-xl flex flex-col overflow-hidden"
               style={{ backgroundColor: currentTheme.surface }}>
            
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b"
                 style={{ backgroundColor: currentTheme.primary, borderColor: currentTheme.border }}>
              <h2 className="text-xl font-semibold" style={{ color: currentTheme.inverseText }}>
                Organization Details
              </h2>
              <button onClick={closeModal} className="p-1 rounded hover:opacity-80 transition-opacity"
                      style={{ color: currentTheme.inverseText }}>
                <FaTimes size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {isDetailsLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: currentTheme.primary }} />
                </div>
              ) : detailsError ? (
                <div className="text-center py-12" style={{ color: currentTheme.error }}>
                  Failed to load organization details
                </div>
              ) : orgDetails ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  
                  {/* Left Column - Profile */}
                  <div className="lg:col-span-1 space-y-6">
                    
                    {/* Profile Card */}
                    <div className="rounded-lg overflow-hidden" style={{ backgroundColor: currentTheme.card }}>
                      <div className="p-6 text-center">
                        <div className="w-28 h-28 mx-auto mb-4 rounded-full overflow-hidden border-4"
                             style={{ borderColor: currentTheme.primary }}>
                          <img
                            src={getFullImageUrl(orgDetails.profile.profile_picture)}
                            alt={orgDetails.profile.organization_name}
                            className="w-full h-full object-cover"
                            onError={(e) => (e.target as HTMLImageElement).src = "/default-avatar.png"}
                          />
                        </div>
                        <h3 className="text-xl font-semibold mb-2" style={{ color: currentTheme.text }}>
                          {orgDetails.profile.organization_name}
                        </h3>
                        <div className="flex flex-col gap-2 items-center">
                          <span className="inline-block px-3 py-1 rounded-full text-xs"
                                style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}>
                            ORGANIZATION
                          </span>
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs ${
                            orgDetails.profile.is_verified ? 'bg-green-500' : 'bg-yellow-500'
                          } text-white`}>
                            {orgDetails.profile.is_verified ? <FaCheck size={12} /> : <FaTimesIcon size={12} />}
                            {orgDetails.profile.is_verified ? 'Verified' : 'Not Verified'}
                          </span>
                        </div>
                      </div>

                      <div className="border-t p-4 space-y-3" style={{ borderColor: currentTheme.border }}>
                        <div className="flex items-start gap-3">
                          <FaPhone className="mt-1" style={{ color: currentTheme.primary }} />
                          <div>
                            <p className="text-xs" style={{ color: currentTheme.textSecondary }}>Phone</p>
                            <p className="text-sm" style={{ color: currentTheme.text }}>
                              {orgDetails.profile.phone_number?.includes('Error') 
                                ? 'Not available' 
                                : orgDetails.profile.phone_number || 'Not provided'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <FaEnvelope className="mt-1" style={{ color: currentTheme.primary }} />
                          <div>
                            <p className="text-xs" style={{ color: currentTheme.textSecondary }}>User ID</p>
                            <p className="text-xs font-mono" style={{ color: currentTheme.text }}>
                              {orgDetails.profile.user}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Documents Card */}
                    {/* {orgDetails.profile.documents && orgDetails.profile.documents.length > 0 && (
                      <div className="rounded-lg overflow-hidden" style={{ backgroundColor: currentTheme.card }}>
                        <div className="px-4 py-3 border-b" style={{ backgroundColor: currentTheme.primary, borderColor: currentTheme.border }}>
                          <h4 className="font-medium flex items-center gap-2" style={{ color: currentTheme.inverseText }}>
                            <FaFile /> Documents ({orgDetails.profile.documents.length})
                          </h4>
                        </div>
                        <div className="p-4">
                          <div className="space-y-2">
                            {orgDetails.profile.documents.map((docId, index) => (
                              <div key={index} className="text-xs p-2 rounded font-mono truncate"
                                   style={{ backgroundColor: currentTheme.surface, color: currentTheme.text }}>
                                {docId}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )} */}
                  </div>

                  {/* Right Column - Address */}
                  <div className="lg:col-span-2 space-y-6">
                    
                    {/* Address Card */}
                    <div className="rounded-lg overflow-hidden" style={{ backgroundColor: currentTheme.card }}>
                      <div className="px-4 py-3 border-b" style={{ backgroundColor: currentTheme.primary, borderColor: currentTheme.border }}>
                        <h4 className="font-medium flex items-center gap-2" style={{ color: currentTheme.inverseText }}>
                          <FaMapMarkerAlt /> Address Details
                        </h4>
                      </div>
                      <div className="p-6 space-y-4">
                        
                        {/* Full Address */}
                        {orgDetails.profile.address && (
                          <div>
                            <p className="text-xs mb-1" style={{ color: currentTheme.textSecondary }}>Complete Address</p>
                            <div className="p-3 rounded" style={{ backgroundColor: currentTheme.surface }}>
                              <p style={{ color: currentTheme.text }}>{orgDetails.profile.address}</p>
                            </div>
                          </div>
                        )}

                        {/* Address Breakdown */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs" style={{ color: currentTheme.textSecondary }}>Street</p>
                            <p className="text-sm" style={{ color: currentTheme.text }}>
                              {orgDetails.profile.street || 'Not provided'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs" style={{ color: currentTheme.textSecondary }}>City</p>
                            <p className="text-sm" style={{ color: currentTheme.text }}>
                              {orgDetails.profile.city || 'Not provided'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs" style={{ color: currentTheme.textSecondary }}>State</p>
                            <p className="text-sm" style={{ color: currentTheme.text }}>
                              {orgDetails.profile.state || 'Not provided'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs" style={{ color: currentTheme.textSecondary }}>Pincode</p>
                            <p className="text-sm" style={{ color: currentTheme.text }}>
                              {orgDetails.profile.pincode || 'Not provided'}
                            </p>
                          </div>
                        </div>

                        {/* Landmark */}
                        {orgDetails.profile.landmark && (
                          <div>
                            <p className="text-xs" style={{ color: currentTheme.textSecondary }}>Landmark</p>
                            <p className="text-sm" style={{ color: currentTheme.text }}>
                              {orgDetails.profile.landmark}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex justify-between" 
                 style={{ borderColor: currentTheme.border, backgroundColor: currentTheme.card }}>
              <button
                onClick={() => setIsPasswordResetModalOpen(true)}
                className="px-4 py-2 rounded-lg font-medium transition-colors hover:opacity-80 flex items-center gap-2"
                style={{ 
                  backgroundColor: currentTheme.surface, 
                  color: currentTheme.primary, 
                  border: `1px solid ${currentTheme.border}`
                }}
              >
                <FaKey size={14} />
                Reset Password
              </button>
              <button
                onClick={closeModal}
                className="px-4 py-2 rounded-lg font-medium transition-colors hover:opacity-80"
                style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="p-6 rounded-lg shadow-xl max-w-sm w-full" style={{ backgroundColor: currentTheme.surface }}>
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4"
                   style={{ backgroundColor: `${currentTheme.error}20` }}>
                <FaExclamationTriangle style={{ color: currentTheme.error }} size={24} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: currentTheme.text }}>Confirm Deletion</h3>
              <p className="mb-6" style={{ color: currentTheme.textSecondary }}>
                Are you sure you want to delete this organization? This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 rounded-lg font-medium transition-colors hover:opacity-80"
                  style={{ backgroundColor: currentTheme.error, color: currentTheme.inverseText }}
                >
                  Delete
                </button>
                <button
                  onClick={() => setDeleteModalOpen(false)}
                  className="px-4 py-2 rounded-lg font-medium transition-colors hover:opacity-80 border"
                  style={{ 
                    backgroundColor: currentTheme.surface, 
                    color: currentTheme.text, 
                    borderColor: currentTheme.border 
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {selectedOrg && orgDetails && (
        <PasswordResetModal
          isOpen={isPasswordResetModalOpen}
          onClose={() => setIsPasswordResetModalOpen(false)}
          onSubmit={handlePasswordReset}
          userName={orgDetails.profile.organization_name}
          userEmail={selectedOrg.email}
          userRole={selectedOrg.role}
        />
      )}
    </div>
  );
};

export default AdminOrganizations;